import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Plus, Trash2, Landmark, FileText, Users } from "lucide-react";

const API = "https://nissi-india.com/api/companyMaster";

function getFinYearId() {
  try {
    const direct = localStorage.getItem("finYearId");
    if (direct) return String(direct);
    const admin = JSON.parse(localStorage.getItem("admin") || "null");
    if (admin?.finYearId) return String(admin.finYearId);
  } catch (_) {}
  return "";
}

function tenantHeaders() {
  const finYearId = getFinYearId();
  return finYearId ? { "x-fin-year-id": finYearId } : {};
}

function tenantParams(extra = {}) {
  const finYearId = getFinYearId();
  return finYearId ? { ...extra, finYearId } : extra;
}

const TABS = [
  { key: "statutory", label: "Statutory", icon: FileText },
  { key: "bank", label: "Bank", icon: Landmark },
  { key: "contact", label: "Contact", icon: Users },
];

const CompanyMaster = () => {
  const hydratingRef = useRef(false);
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("statutory");
  const [companyId, setCompanyId] = useState(0);
  const [exists, setExists] = useState(false);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);

  const [modeState, setModeState] = useState("select");
  const [modeDistrict, setModeDistrict] = useState("select");
  const [modeCity, setModeCity] = useState("select");
  const [isHydrating, setIsHydrating] = useState(false);

  const inputClass = isDark
    ? "w-full mt-1 p-2 rounded-lg border border-white/15 bg-gray-800 text-white placeholder-white/40"
    : "w-full mt-1 p-2 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400";

  const [form, setForm] = useState({
    companyName: "",
    brandName: "",
    address1: "",
    address2: "",
    countryId: "",
    stateId: "",
    districtId: "",
    cityId: "",
    stateName: "",
    districtName: "",
    cityName: "",
    zipcode: "",
    phone: "",
    mobile: "",
    email: "",
    remarks: "",
    logoUrl: "",
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [tabRows, setTabRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const canUseTabs = companyId > 0;
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const getLogoUrl = (cid) =>
    cid
      ? `${API}/company/logo?id=${cid}&finYearId=${encodeURIComponent(getFinYearId())}&t=${Date.now()}`
      : "";

  const loadCountries = async () => {
    const r = await axios.get(`${API}/countries`, {
      headers: tenantHeaders(),
      params: tenantParams(),
    });
    setCountries(r.data.data || []);
  };

  const loadCompany = async () => {
    const r = await axios.get(`${API}/company`, {
      headers: tenantHeaders(),
      params: tenantParams(),
    });

    if (!r.data.exists) {
      setExists(false);
      setCompanyId(0);
      return;
    }

    hydratingRef.current = true;
    setIsHydrating(true);

    setExists(true);
    setCompanyId(r.data.companyId);

    const c = r.data.company;

    setForm((p) => ({
      ...p,
      companyName: c.companyName || "",
      brandName: c.brandName || "",
      address1: c.address1 || "",
      address2: c.address2 || "",
      countryId: String(c.countryId || ""),
      stateId: String(c.stateId || ""),
      districtId: String(c.districtId || ""),
      cityId: String(c.cityId || ""),
      zipcode: c.zipcode || "",
      phone: c.phone || "",
      mobile: c.mobile || "",
      email: c.email || "",
      remarks: c.remarks || "",
      logoUrl: c.logoUrl || "",
    }));

    setLogoPreview(getLogoUrl(r.data.companyId) || c.logoUrl || "");

    const countryId = String(c.countryId || "");
    const stateId = String(c.stateId || "");
    const districtId = String(c.districtId || "");

    if (countryId) await loadStates(countryId);
    if (stateId) await loadDistricts(stateId);
    if (districtId) await loadCities(districtId);

    hydratingRef.current = false;
    setIsHydrating(false);
  };

  const loadStates = async (countryId) => {
    if (!countryId) return;
    const r = await axios.get(`${API}/states`, {
      headers: tenantHeaders(),
      params: tenantParams({ countryId }),
    });
    setStates(r.data.data || []);
  };

  const loadDistricts = async (stateId) => {
    if (!stateId) return;
    const r = await axios.get(`${API}/districts`, {
      headers: tenantHeaders(),
      params: tenantParams({ stateId }),
    });
    setDistricts(r.data.data || []);
  };

  const loadCities = async (districtId) => {
    if (!districtId) return;
    const r = await axios.get(`${API}/cities`, {
      headers: tenantHeaders(),
      params: tenantParams({ districtId }),
    });
    setCities(r.data.data || []);
  };

  const loadTab = async (tabKey) => {
    if (!companyId) {
      setTabRows([]);
      return;
    }
    setSelectedIds(new Set());
    let url = "";
    if (tabKey === "statutory") url = `${API}/company/${companyId}/statutory`;
    if (tabKey === "bank") url = `${API}/company/${companyId}/banks`;
    if (tabKey === "contact") url = `${API}/company/${companyId}/contacts`;

    const r = await axios.get(url, {
      headers: tenantHeaders(),
      params: tenantParams(),
    });
    setTabRows(r.data.data || []);
  };

  useEffect(() => {
    (async () => {
      try {
        if (!getFinYearId()) {
          toast.error("Please login and select financial year");
          navigate("/");
          return;
        }
        await loadCountries();
        await loadCompany();
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.error || "Failed to load company data");
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (hydratingRef.current) return;
      if (!form.countryId) return;
      try {
        setStates([]);
        setDistricts([]);
        setCities([]);
        setField("stateId", "");
        setField("districtId", "");
        setField("cityId", "");
        await loadStates(form.countryId);
      } catch (e) {
        toast.error("Failed to load states");
      }
    })();
  }, [form.countryId]);

  useEffect(() => {
    (async () => {
      if (hydratingRef.current) return;
      if (modeState !== "select") return;
      if (!form.stateId) return;
      try {
        setDistricts([]);
        setCities([]);
        setField("districtId", "");
        setField("cityId", "");
        await loadDistricts(form.stateId);
      } catch (e) {
        toast.error("Failed to load districts");
      }
    })();
  }, [form.stateId, modeState]);

  useEffect(() => {
    (async () => {
      if (hydratingRef.current) return;
      if (modeDistrict !== "select") return;
      if (!form.districtId) return;
      try {
        setCities([]);
        setField("cityId", "");
        await loadCities(form.districtId);
      } catch (e) {
        toast.error("Failed to load cities");
      }
    })();
  }, [form.districtId, modeDistrict]);

  useEffect(() => {
    (async () => {
      try {
        await loadTab(activeTab);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load tab data");
      }
    })();
  }, [companyId, activeTab]);

  const validateForm = () => {
    if (!form.companyName.trim()) return toast.error("Please enter Company Name"), false;
    if (!form.address1.trim()) return toast.error("Please enter Address1"), false;
    if (!form.countryId) return toast.error("Please Select Country"), false;

    if (modeState === "select") {
      if (!form.stateId) return toast.error("Please Select State"), false;
    } else if (!form.stateName.trim()) {
      return toast.error("Please enter State"), false;
    }

    if (modeDistrict === "select") {
      if (!form.districtId) return toast.error("Please Select district"), false;
    } else if (!form.districtName.trim()) {
      return toast.error("Please enter District"), false;
    }

    if (!form.zipcode.trim()) return toast.error("Please enter ZipCode"), false;

    if (form.email.trim()) {
      const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailPattern.test(form.email.trim())) return toast.error("Please enter valid emailid"), false;
    }

    if (!form.phone.trim()) return toast.error("Please enter Phone"), false;

    const allowed = "0123456789()-, ";
    for (const ch of form.phone) {
      if (!allowed.includes(ch)) return toast.error("Please Enter a valid Phone Number"), false;
    }

    if (!exists && !logoFile) return toast.error("Please provide jpg images"), false;

    return true;
  };

  const onSave = async () => {
    try {
      if (!validateForm()) return;

      const fd = new FormData();
      if (logoFile) fd.append("logo", logoFile);

      const safeNum = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      };

      fd.append(
        "payload",
        JSON.stringify({
          finYearId: Number(getFinYearId()) || undefined,
          isUpdate: exists,
          companyId,
          modeState,
          modeDistrict,
          modeCity,
          companyName: form.companyName,
          brandName: form.brandName,
          address1: form.address1,
          address2: form.address2,
          cityId: safeNum(form.cityId),
          districtId: safeNum(form.districtId),
          stateId: safeNum(form.stateId),
          countryId: safeNum(form.countryId),
          stateName: form.stateName,
          districtName: form.districtName,
          cityName: form.cityName,
          zipcode: form.zipcode,
          phone: form.phone,
          mobile: form.mobile,
          email: form.email,
          remarks: form.remarks,
        })
      );

      const r = await axios.post(`${API}/company/save`, fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...tenantHeaders(),
        },
      });

      if (r.data.ok) {
        const newId = r.data.companyId || 1;
        setCompanyId(newId);
        setExists(true);
        toast.success(r.data.mode === "insert" ? "Inserted Successfully" : "Updated Successfully");
        await loadCompany();
        await loadTab(activeTab);
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || "Save failed");
    }
  };

  const onPickLogo = (file) => {
    if (!file) return;
    const name = (file.name || "").toLowerCase();
    if (!(name.endsWith(".jpg") || name.endsWith(".jpeg"))) {
      toast.error("Please provide jpg images");
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onDeleteSelected = async () => {
    if (!canUseTabs) return toast.error("Please create or select Company first");
    if (selectedIds.size === 0) return toast.error("Please select any One");
    if (!window.confirm("Are you sure to delete?")) return;

    let url = "";
    if (activeTab === "statutory") url = `${API}/company/${companyId}/statutory`;
    if (activeTab === "bank") url = `${API}/company/${companyId}/banks`;
    if (activeTab === "contact") url = `${API}/company/${companyId}/contacts`;

    try {
      await axios.delete(url, {
        headers: tenantHeaders(),
        params: tenantParams(),
        data: { ids: Array.from(selectedIds) },
      });
      toast.success("Deleted Successfully");
      await loadTab(activeTab);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || "Delete failed");
    }
  };

  const onAdd = () => {
    if (!canUseTabs) return toast.error("Please create or select Company first");
    if (activeTab === "statutory") navigate("/adminMaster/company-statutory-add");
    if (activeTab === "bank") navigate("/adminMaster/company-bank-add");
    if (activeTab === "contact") navigate("/adminMaster/company-contact-add");
  };

  const columns = useMemo(() => {
    if (activeTab === "statutory") {
      return [
        { key: "rowno", label: "S.No" },
        { key: "fld_statutoryhead", label: "Statutory Head", clickKey: "fld_statutoryid" },
        { key: "fld_statutorydetail", label: "Details" },
      ];
    }
    if (activeTab === "bank") {
      return [
        { key: "rowno", label: "S.No" },
        { key: "BankCode", label: "Bank", clickKey: "fld_detailid" },
        { key: "AccountNo", label: "Account No" },
      ];
    }
    return [
      { key: "rowno", label: "S.No" },
      { key: "ContactName", label: "Contact Person", clickKey: "fld_contactid" },
      { key: "Designation", label: "Designation" },
    ];
  }, [activeTab]);

  const onRowClick = (row) => {
    if (!canUseTabs) return toast.error("Please create or select Company first");
    if (activeTab === "statutory") navigate(`/adminMaster/company-statutory-add?statutoryId=${row.fld_statutoryid}`);
    if (activeTab === "bank") navigate(`/adminMaster/company-bank-add?bankId=${row.fld_detailid}`);
    if (activeTab === "contact") navigate(`/adminMaster/company-contact-add?contactId=${row.fld_contactid}`);
  };

  return (
    <AdminLayoutWrapper>
      <ToastContainer />
      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">COMPANY MASTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold">Company Name *</label>
              <input
                className={inputClass}
                value={form.companyName}
                onChange={(e) => setField("companyName", e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-semibold">Brand Name</label>
              <input
                className={inputClass}
                value={form.brandName}
                onChange={(e) => setField("brandName", e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-semibold">Address 1 *</label>
              <input
                className={inputClass}
                value={form.address1}
                onChange={(e) => setField("address1", e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-semibold">Zipcode *</label>
              <input
                className={inputClass}
                maxLength={6}
                value={form.zipcode}
                onChange={(e) => setField("zipcode", e.target.value.replace(/[^\d]/g, ""))}
              />
            </div>

            <div>
              <label className="text-sm font-semibold">Address 2</label>
              <input
                className={inputClass}
                value={form.address2}
                onChange={(e) => setField("address2", e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-semibold">Email</label>
              <input
                className={inputClass}
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-semibold">Country *</label>
              <select
                className={inputClass}
                value={form.countryId}
                onChange={(e) => setField("countryId", e.target.value)}
              >
                <option value="">Select</option>
                {countries.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-semibold">Phone *</label>
              <input
                className={inputClass}
                maxLength={11}
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-semibold">State *</label>
              {modeState === "select" ? (
                <select
                  className={inputClass}
                  value={form.stateId}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "OTHERS") {
                      setModeState("text");
                      setField("stateId", "");
                      setField("stateName", "");
                      setModeDistrict("text");
                      setField("districtId", "");
                      setField("districtName", "");
                      setModeCity("text");
                      setField("cityId", "");
                      setField("cityName", "");
                      return;
                    }
                    setField("stateId", v);
                  }}
                >
                  <option value="">Select</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                  <option value="OTHERS">Others</option>
                </select>
              ) : (
                <input
                  className={inputClass}
                  placeholder="Enter State"
                  value={form.stateName}
                  onChange={(e) => setField("stateName", e.target.value)}
                />
              )}
            </div>

            <div>
              <label className="text-sm font-semibold">Mobile</label>
              <input
                className={inputClass}
                maxLength={10}
                value={form.mobile}
                onChange={(e) => setField("mobile", e.target.value.replace(/[^\d]/g, ""))}
              />
            </div>

            <div>
              <label className="text-sm font-semibold">District *</label>
              {modeDistrict === "select" ? (
                <select
                  className={inputClass}
                  value={form.districtId}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "OTHERS") {
                      setModeDistrict("text");
                      setField("districtId", "");
                      setField("districtName", "");
                      setModeCity("text");
                      setField("cityId", "");
                      setField("cityName", "");
                      return;
                    }
                    setField("districtId", v);
                  }}
                >
                  <option value="">Select</option>
                  {districts.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                  <option value="OTHERS">Others</option>
                </select>
              ) : (
                <input
                  className={inputClass}
                  placeholder="Enter District"
                  value={form.districtName}
                  onChange={(e) => setField("districtName", e.target.value)}
                />
              )}
            </div>

            <div>
              <label className="text-sm font-semibold">City</label>
              {modeCity === "select" ? (
                <select
                  className={inputClass}
                  value={form.cityId}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "OTHERS") {
                      setModeCity("text");
                      setField("cityId", "");
                      setField("cityName", "");
                      return;
                    }
                    setField("cityId", v);
                  }}
                >
                  <option value="">Select</option>
                  {cities.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                  <option value="OTHERS">Others</option>
                </select>
              ) : (
                <input
                  className={inputClass}
                  placeholder="Enter City"
                  value={form.cityName}
                  onChange={(e) => setField("cityName", e.target.value)}
                />
              )}
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-semibold">Remarks</label>
              <textarea
                className={inputClass}
                rows={3}
                value={form.remarks}
                onChange={(e) => setField("remarks", e.target.value)}
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-semibold">Logo {exists ? "" : "*"}</label>
              <div className="flex flex-col md:flex-row gap-4 mt-2 items-start">
                <input
                  type="file"
                  accept=".jpg,.jpeg"
                  onChange={(e) => onPickLogo(e.target.files?.[0])}
                />
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="h-16 w-16 object-contain rounded border"
                  />
                ) : null}
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-center">
            <button
              onClick={onSave}
              className={`relative group px-10 py-3 rounded-xl font-semibold text-white tracking-wide ${buttonClass} ${hoverClass} shadow-lg transition-all duration-300 hover:shadow-2xl hover:-translate-y-0.5 active:translate-y-0 focus:outline-none`}
            >
              <span className={`absolute inset-0 rounded-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 ${buttonClass} blur-lg`} />
              <span className="relative flex items-center gap-2">
                {exists ? "Update" : "Create Company"}
              </span>
            </button>
          </div>

          <div className="mt-8">
            <div
              className={`inline-flex p-1 rounded-2xl border ${
                isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"
              }`}
            >
              {TABS.map((t) => {
                const ActiveIcon = t.icon;
                const isActive = activeTab === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => {
                      if (!companyId) return toast.error("Please create Company first");
                      setActiveTab(t.key);
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-medium transition-all duration-300 ${
                      isActive
                        ? `${buttonClass} text-white shadow-md`
                        : isDark
                        ? "text-white/80 hover:bg-white/10"
                        : "text-gray-700 hover:bg-white"
                    }`}
                  >
                    <ActiveIcon className="w-4 h-4" />
                    {t.label}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <button
                  onClick={onAdd}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-white font-medium shadow ${buttonClass} ${hoverClass} transition-all duration-200 hover:-translate-y-0.5`}
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
                <button
                  onClick={onDeleteSelected}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium shadow transition-all duration-200 hover:-translate-y-0.5"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove
                </button>
              </div>
              <div className={`${isDark ? "text-white/70" : "text-gray-600"} text-sm`}>
                Selected: <span className="font-semibold">{selectedIds.size}</span>
              </div>
            </div>

            <div
              className={`mt-4 overflow-x-auto rounded-2xl border shadow-sm ${
                isDark ? "border-white/10 bg-white/10 text-gray-200" : "border-gray-200 bg-white text-gray-900"
              }`}
            >
              <table className="min-w-full text-sm text-left">
                <thead className={`text-xs font-semibold uppercase ${isDark ? "bg-white/10 text-gray-100" : "bg-gray-100 text-gray-700"}`}>
                  <tr>
                    <th className={`px-4 py-3 sticky left-0 z-10 ${isDark ? "bg-gray-900/60" : "bg-gray-100"}`}>
                      Select
                    </th>
                    {columns.map((c) => (
                      <th key={c.key} className="px-4 py-3 whitespace-nowrap">
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tabRows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length + 1} className={`px-4 py-6 text-center ${isDark ? "text-gray-300" : "text-gray-500"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    tabRows.map((row, idx) => {
                      const rowId =
                        activeTab === "statutory"
                          ? row.fld_statutoryid
                          : activeTab === "bank"
                          ? row.fld_detailid
                          : row.fld_contactid;
                      const zebra =
                        idx % 2 === 0
                          ? isDark
                            ? "bg-white/0"
                            : "bg-white"
                          : isDark
                          ? "bg-white/5"
                          : "bg-gray-50";
                      return (
                        <tr
                          key={rowId || idx}
                          className={`${zebra} ${isDark ? "hover:bg-white/10" : "hover:bg-gray-100"} transition-colors`}
                        >
                          <td className={`px-4 py-3 sticky left-0 ${isDark ? "bg-gray-900/60" : "bg-white"}`}>
                            <input
                              type="checkbox"
                              checked={selectedIds.has(rowId)}
                              onChange={() => toggleSelect(rowId)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                          {columns.map((c) => (
                            <td
                              key={c.key}
                              className={`px-4 py-3 whitespace-nowrap ${
                                c.clickKey
                                  ? isDark
                                    ? "cursor-pointer text-blue-300 underline underline-offset-4 hover:text-blue-200"
                                    : "cursor-pointer text-blue-700 underline underline-offset-4 hover:text-blue-900"
                                  : ""
                              }`}
                              onClick={() => (c.clickKey ? onRowClick(row) : null)}
                            >
                              {row[c.key] ?? "-"}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </AdminLayoutWrapper>
  );
};

export default CompanyMaster;